// Ch78 · THE LEADS PAGE.
//
// Grant's ruling (2026-09-08): "Messages = REAL clients. I WANT A REAL LEADS
// PAGE … DESIGNED WELL AND INFORMATIVE … EVERYTHING YOU COULD WANT TO KNOW
// ABOUT OUR LEADS", and "we don't need the top cards — we aren't going to have
// good data yet."
//
// So there are no KPI tiles. The page is two things, in the order they are
// worked: the move-in calendar (the date the business runs on) and the work
// list (who is stuck where, and the one thing to do about it). Everything each
// widget means lives in an (i) hover, not in a paragraph above it.
//
// One population, defined once in lib/leadsWork.ts and shared by both halves:
// every live lead, plus every client still at `new_client` — converted, but
// never yet engaged. That is Grant's line exactly: a person who said yes and
// was never spoken to is still a lead.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Grid, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import type { Agent, Client, Lead } from "./types";
import LeadsFilterBar from "./LeadsFilterBar";
import SendFromRowDialog from "./SendFromRowDialog";
import LeadsMoveInCalendar from "./LeadsMoveInCalendar";
import LeadsWorkList from "./LeadsWorkList";
import type { Sender } from "../../lib/leadsSend";
import { fetchAgentSendEnabled } from "../../services/systemCapabilities";
import LeadFunnelCard from "./LeadFunnelCard";
import LeadClaimInbox from "./LeadClaimInbox";
import LeadsInsightCards from "./LeadsInsightCards";
import { buildSources, buildTiming, buildWants } from "../../lib/leadsInsights";
import { fetchLeadFunnel, type LeadFunnel } from "../../services/leadFunnelService";
import { isCalendarPerson } from "../../lib/leadsCalendar";
import { buildBoardPeople, type BoardPerson } from "../../lib/moveInBoard";
import {
  buildWorkGroups,
  buildWorkRows,
  nextAction,
  type LeadClaimRow,
  type WorkRow,
} from "../../lib/leadsWork";
import { fetchClientProfile } from "../../services/clientProfileService";
import { fetchClientQueue } from "../../services/clientQueueService";
import type { QueueClient } from "../messages/clientQueue";
import { runLeadAction, LeadConflictError } from "../../services/leadConversionService";
import { ApiError, apiErrorDetail, refusalMessage } from "../../lib/api";
import { updateLeadById } from "./clientMutations";
import { supabase } from "../../lib/supabase";
import { runBulk, type BulkAction } from "../../lib/leadsBulk";

export interface LeadsPageProps {
  leads: Lead[];
  clients: Client[];
  agents: Agent[];
  /** The claim ledger (migration 049). null when the read failed — the page
   * says so rather than showing every claimed lead as unclaimed. */
  claims: LeadClaimRow[] | null;
  claimsError: boolean;
  onOpenLead: (lead: Lead) => void;
  onOpenClient: (client: Client) => void;
  /** A claim or a conversion landed — the owner of the data must refetch. */
  onLeadsChanged: () => void;
  /** Assigning a lead to somebody else is an admin override server-side. */
  canAssign?: boolean;
  /** Signs the drafted opener. Absent signs as the business alone. */
  senderName?: string | null;
  /** PKT-0666 · the viewer's id and role. The page offers a Send only where the
   *  route would accept one; without these it cannot tell, so it offers none. */
  senderId?: string | null;
  senderRole?: string | null;
  /** Injected for deterministic tests; defaults to the wall clock. */
  now?: Date;
}

const DAY_MS = 86_400_000;
/** How many living profiles the page will read for the understanding bars.
 * Bounded on purpose: one request per client row, and this page is the top of
 * the funnel, not the client book. Rows beyond it show "—", never a zero. */
const PROFILE_FETCH_LIMIT = 40;

/**
 * PKT-0666 §F1 · what each refusal CODE means, in a sentence a locator can act on.
 *
 * `routes/lead_conversion.py:47` raises `HTTPException(status, detail={"code": code})`
 * — a code and NOTHING else. There is no `message` key for any of the seven codes it
 * can emit (`lead_not_found`, `lead_missing_required_identity`, `admin_override_required`,
 * `idempotency_payload_mismatch`, `next_action_integrity_error`,
 * `conversion_integrity_error`, and the `lead_action_failed` fallback at `:45`), nor for
 * the `admin_override_required` raised early at `:34`. So `refusalMessage` — which needs
 * `detail` to be a string or to carry `message` — returns null on EVERY one of them, and
 * before this map the `??` fell through to `ApiError.message`, which is only
 * `API error: <status>`. That is the bare status code this whole round existed to remove,
 * and it was live for six of the seven codes: only `ownership_conflict` (which comes from
 * the OTHER raise, `:49`, carrying the RPC result) was read by name.
 *
 * The map lives on the frontend rather than adding `"message"` to the route because this
 * packet is frontend-only; if the route ever does send a sentence, `refusalMessage` wins
 * ahead of this map and the server keeps the last word on its own wording.
 *
 * `ownership_conflict` is deliberately absent: the branch above this one already names the
 * owner, which is strictly better than any sentence written here could be.
 */
const CLAIM_REFUSAL: Record<string, string> = {
  lead_not_found: "This lead no longer exists — refresh the list and try again.",
  lead_missing_required_identity:
    "This lead has no name or email yet, so it cannot be claimed. Add one and try again.",
  admin_override_required: "Only an admin can claim a lead for somebody else.",
  idempotency_payload_mismatch:
    "This claim was already sent with different details. Refresh the list and try again.",
  next_action_integrity_error:
    "The lead was claimed but its next step could not be created. Tell support before retrying.",
  conversion_integrity_error:
    "This lead could not be turned into a client. Tell support before retrying.",
  lead_action_failed: "The claim service did not answer. Try again in a moment.",
};

/**
 * Ch87 · PKT-0688 · when the wait stops being "normal" and starts needing a word.
 *
 * MEASURED in production (2026-09-11 ~23:35 EDT, host named in PKT-0688): the
 * mount-to-request half of this wait ran 732/1608/2107/7327/7784 ms over five
 * warm navigations, and the /api/clients/queue call itself took 34362 ms on a
 * cold load — about 38 seconds end to end, and it DID finish. That is the whole
 * bug: it was slow, not dead, and static text with no motion for 38 seconds
 * reads as a frozen app. Grant reasonably concluded the page was broken while
 * it was working.
 *
 * 10s sits above the worst healthy session read I measured (7784 ms), so this
 * does not cry wolf on an ordinary slow load, and far below the ~38s that made
 * the page read as dead. It escalates the WORDS only — it cancels nothing and
 * fails nothing. The page still waits, because the waiting was never the bug.
 */
const QUEUE_SLOW_NOTICE_MS = 10_000;

export default function LeadsPage({
  leads,
  clients,
  agents,
  claims,
  claimsError,
  onOpenLead,
  onOpenClient,
  onLeadsChanged,
  canAssign = false,
  senderName,
  senderId,
  senderRole,
  now,
}: LeadsPageProps) {
  const today = useMemo(() => now ?? new Date(), [now]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ realOnly: false, textsOk: false, next30: false });
  const [understanding, setUnderstanding] = useState<Map<string, number | null>>(new Map());
  const [focus, setFocus] = useState<{ key: string; ms: number } | null>(null);
  // Ch83 · the person the composer is open on. One at a time, by design.
  const [sendingTo, setSendingTo] = useState<WorkRow | null>(null);

  // PKT-0666 · whether a locator may text at all is a property of the DEPLOY
  // (SMS_AGENT_SEND_ENABLED), not of this browser. null until the answer lands,
  // and null is NOT permission — an agent sees a named refusal rather than a
  // button that might 403. Elevated roles never depend on it.
  const [agentSendEnabled, setAgentSendEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    fetchAgentSendEnabled().then((v) => {
      if (live) setAgentSendEnabled(v);
    });
    return () => {
      live = false;
    };
  }, []);
  // PKT-0666 §F1 · "Claim to text". Grant ruled "assigned only", and a lead has
  // no owner until it is claimed, so first contact would otherwise be
  // impossible. One click claims AND converts (049's atomic RPC), then the
  // composer opens on the client it became.
  //
  // The composer cannot open synchronously: the client row does not exist in
  // this page's props until the owner refetches. So the new client's id is
  // parked, and an effect opens the composer once that row actually arrives.
  // Parking an id rather than fabricating a row keeps one definition of a
  // person — the page never invents one it has not read.
  const [pendingComposeClientId, setPendingComposeClientId] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);

  const claimToText = async (row: WorkRow) => {
    const leadId = (row.person.lead as Lead | undefined)?.id;
    if (!leadId) return;
    setClaimError(null);
    try {
      const result = await runLeadAction(leadId, "convert");
      // Only the missing-client_id half can actually happen. `lead_conversion.py:48-49`
      // raises 409 whenever the RPC answers `ok:false`, so a not-ok result never
      // returns as a RESOLVED promise — every refusal arrives in the catch below.
      // The `!result.ok` half of this guard, and the `owner_full_name` sentence
      // behind it, were therefore dead: the message that LOOKED like it named the
      // owner could never render, while the one that did run was `e.message`.
      // A resolved result with no client_id is reachable (an idempotent replay of
      // a bare claim returns `code:"claimed"` with a null converted_client_id),
      // and it has no client for the composer to open on, so it still needs saying.
      if (!result.client_id) {
        setClaimError(`${row.person.name} could not be claimed.`);
        onLeadsChanged();
        return;
      }
      setPendingComposeClientId(result.client_id);
      onLeadsChanged();
    } catch (e) {
      // A second locator can claim the same lead a heartbeat earlier, and a
      // lead with no name or email cannot be converted at all
      // (`lead_missing_required_identity`). Both are the server's answer and
      // both are said in its words.
      //
      // Ch83's bug, fifth copy — read the BODY, not the message.
      // `ApiError`'s message is only `API error: <status>` (lib/api.ts:100),
      // and leadConversionService only builds a `LeadConflictError` when the
      // message contains a `{`, which it never does. So on the real refusal
      // path that branch cannot run, and `e.message` put the literal string
      // "API error: 409" under the work list — on the one click standing
      // between a locator and first contact. `apiErrorDetail`/`refusalMessage`
      // were written for exactly this (lib/api.ts:502) and the sibling
      // composer already uses them (SendFromRowDialog.tsx:145).
      //
      // Both arms are kept and both say the SAME sentence: the service may yet
      // be fixed for its five callers, and a refusal must not change its words
      // depending on which layer noticed it.
      const detail = apiErrorDetail(e);
      const conflict =
        e instanceof LeadConflictError
          ? e.result
          : detail?.code === "ownership_conflict"
            ? detail
            : null;
      if (conflict) {
        const who = conflict.owner_full_name || "another locator";
        setClaimError(`${row.person.name} was just claimed by ${who}.`);
      } else {
        // The order is deliberate. The server's own sentence first, if it ever
        // sends one; then what the CODE means (CLAIM_REFUSAL, above — the route
        // sends a code and nothing else, so without this every non-conflict
        // refusal read "API error: 409"); then, for a code we have never seen,
        // still a sentence naming it rather than a status number; and only for a
        // non-API failure — a timeout, a dropped connection — `e.message`, which
        // for those classes is genuinely descriptive.
        const code = typeof detail?.code === "string" ? detail.code : null;
        const said =
          refusalMessage(e) ??
          (code
            ? (CLAIM_REFUSAL[code] ??
              `${row.person.name} could not be claimed — the server refused with "${code}". Tell support if it keeps happening.`)
            : null);
        setClaimError(
          said ??
            (e instanceof ApiError
              ? `${row.person.name} could not be claimed — the server refused (${e.status}) without saying why. Tell support if it keeps happening.`
              : e instanceof Error
                ? e.message
                : String(e)),
        );
      }
      onLeadsChanged();
    }
  };

  const sender: Sender = useMemo(
    () => ({ id: senderId ?? null, role: senderRole ?? null, agentSendEnabled }),
    [senderId, senderRole, agentSendEnabled],
  );
  // The living profile's own next question per client — it beats a generic verb.
  const [asks, setAsks] = useState<Map<string, string | null>>(new Map());
  // Engagement signals from GET /clients/queue. `undefined` = still loading,
  // `null` = the read FAILED and the partition is running degraded.
  const [signals, setSignals] = useState<Map<string, QueueClient> | null | undefined>(undefined);
  // Has the queue read been outstanding long enough to deserve a sentence?
  // Purely cosmetic escalation: it never cancels the read.
  const [queueWaitLong, setQueueWaitLong] = useState(false);
  // The window the ANALYTICS read over. Deliberately not applied to the work
  // list: hiding a lead that has been waiting 40 days because the range says 30
  // would hide exactly the row that most needs working.
  const [rangeDays, setRangeDays] = useState<number>(30);
  const [funnel, setFunnel] = useState<LeadFunnel | null>(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [funnelError, setFunnelError] = useState<string | null>(null);
  const workListRef = useRef<HTMLDivElement | null>(null);

  const allPeople = useMemo(
    () => buildBoardPeople(leads, clients, agents),
    [leads, clients, agents],
  );
  // DEFECT B · `asks` used to sit in this dependency list. It only changes the
  // TEXT of a next action, but it arrives 40 HTTP requests late — so when the
  // living-profile reads landed, the whole population was rebuilt, `people` got
  // a new identity, and the calendar re-derived every cell for a caption
  // change. The population is computed once and the labels are layered on top.
  const baseRows = useMemo(
    () => buildWorkRows(allPeople, claims, today, signals ?? null),
    [allPeople, claims, today, signals],
  );

  useEffect(() => {
    if (!pendingComposeClientId) return;
    const arrived = baseRows.find((r) => r.key === `client:${pendingComposeClientId}`);
    if (!arrived) return; // still refetching — wait rather than guess
    setSendingTo(arrived);
    setPendingComposeClientId(null);
  }, [pendingComposeClientId, baseRows]);

  const allRows = useMemo(
    () =>
      asks.size === 0
        ? baseRows
        : baseRows.map((r) =>
            r.person.kind === "client" && r.person.client
              ? { ...r, nextAction: nextAction(r.stage, r.person, asks.get(r.person.client.id) ?? null) }
              : r,
          ),
    [baseRows, asks],
  );

  /** The Leads-page population — from baseRows, so a late caption cannot move it. */
  const people = useMemo(() => baseRows.map((r) => r.person), [baseRows]);
  const expiredKeys = useMemo(
    () => new Set(allRows.filter((r) => r.stage === "expired").map((r) => r.key)),
    [allRows],
  );

  const agentNames = useMemo(
    () => new Map(agents.map((a) => [a.id, a.full_name || a.email])),
    [agents],
  );

  // The understanding bar's data. One read per client row, capped, failures
  // stored as null so the bar renders "—" instead of an invented number.
  //
  // The effect depends on the JOINED ids, not on `allRows`: re-sorting the list
  // (a filter, a search keystroke) must not re-fetch every profile, and a
  // dependency array that says so honestly needs no eslint escape hatch.
  const profileClientIds = useMemo(
    () =>
      allRows
        .filter((r) => r.person.kind === "client" && r.person.client)
        .slice(0, PROFILE_FETCH_LIMIT)
        .map((r) => (r.person.client as Client).id)
        .sort()
        .join("|"),
    [allRows],
  );

  useEffect(() => {
    const ids = profileClientIds ? profileClientIds.split("|") : [];
    if (ids.length === 0) return;
    let cancelled = false;
    void Promise.all(
      ids.map(async (id) => {
        try {
          const profile = await fetchClientProfile(id);
          return [
            id,
            { score: profile.understanding.score, ask: profile.ask_next?.question ?? null },
          ] as const;
        } catch {
          return [id, null] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setUnderstanding(new Map(entries.map(([id, e]) => [id, e ? e.score : null])));
      setAsks(new Map(entries.map(([id, e]) => [id, e ? e.ask : null])));
    });
    return () => {
      cancelled = true;
    };
  }, [profileClientIds]);

  useEffect(() => {
    if (signals !== undefined) {
      setQueueWaitLong(false);
      return;
    }
    const timer = setTimeout(() => setQueueWaitLong(true), QUEUE_SLOW_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [signals]);

  // Who is a real client rather than a lead, read from the definition Messages
  // uses. A failed read is reported, not silently downgraded: without it a
  // converted lead who already TEXTED BACK would sit on this page as work.
  useEffect(() => {
    let cancelled = false;
    fetchClientQueue("all")
      .then((res) => {
        if (!cancelled) setSignals(new Map(res.clients.map((c) => [c.id, c])));
      })
      .catch(() => {
        if (!cancelled) setSignals(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setFunnelLoading(true);
    setFunnelError(null);
    fetchLeadFunnel(rangeDays)
      .then((data) => {
        if (!cancelled) setFunnel(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFunnel(null);
        setFunnelError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setFunnelLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rangeDays]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const todayMs = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    return allRows.filter((r) => {
      if (filters.realOnly && r.person.tone !== "real") return false;
      if (filters.textsOk && !r.person.textsOk) return false;
      if (filters.next30) {
        if (r.person.dateMs === null) return false;
        if (r.person.dateMs < todayMs || r.person.dateMs > todayMs + 30 * DAY_MS) return false;
      }
      if (!q) return true;
      const lead = r.person.lead;
      const client = r.person.client;
      const haystack = [
        r.person.name,
        lead?.email,
        lead?.phone,
        client?.email,
        client?.phone,
        r.sourceLabel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allRows, search, filters, today]);

  /** The calendar draws exactly what the list lists — one filter, one page. */
  const filteredPeople = useMemo(() => filtered.map((r) => r.person), [filtered]);
  const groups = useMemo(() => buildWorkGroups(filtered), [filtered]);

  // The analytics range, applied ONLY where a window is the honest denominator:
  // "where did recent arrivals come from" and "what does recent demand want".
  // Timing is deliberately all-time — it is the calendar's own distribution,
  // and a lead moving in eight months is not noise, it is the pipeline.
  const rangeRows = useMemo(() => {
    const cutoff = today.getTime() - rangeDays * DAY_MS;
    return allRows.filter((r) => {
      const arrived = Date.parse(r.person.lead?.created_at ?? r.person.client?.created_at ?? "");
      // An unreadable arrival time is not "old" — it is unknown, and dropping it
      // silently would shrink a denominator nobody can see.
      return !Number.isFinite(arrived) || arrived >= cutoff;
    });
  }, [allRows, rangeDays, today]);

  const sources = useMemo(() => buildSources(rangeRows), [rangeRows]);
  const wants = useMemo(() => buildWants(rangeRows), [rangeRows]);
  const timing = useMemo(() => buildTiming(people, today), [people, today]);
  const liveCount = allRows.filter((r) => r.stage !== "expired").length;
  const expiredCount = expiredKeys.size;

  // The inbox and the work list's "Unclaimed" group are the SAME rows read
  // from the same ledger — one definition, two renderings, so they cannot
  // disagree about who is waiting.
  // The inbox reads the FILTERED rows like everything else. It used to read
  // allRows, which made it the third population on one page: search "aus" and
  // the bar said "showing 0 of 70", the table said "Nobody matches", and the
  // inbox went on listing three people to claim.
  const inboxRows = useMemo(
    () => filtered.filter((r) => r.stage === "unclaimed" && r.person.lead),
    [filtered],
  );

  const openPerson = (p: BoardPerson) => {
    if (p.kind === "client" && p.client) onOpenClient(p.client);
    else if (p.lead) onOpenLead(p.lead);
  };
  const openRow = (row: WorkRow) => openPerson(row.person);

  const assignableAgents = useMemo(
    () => agents.map((a) => ({ id: a.id, name: a.full_name || a.email })),
    [agents],
  );

  // Bulk writes go through the same two canonical writers every other surface
  // uses: the claim/convert RPC, and updateLeadById for the lead row. No third
  // spelling of "archive a lead" is invented here.
  const handleBulk = (action: BulkAction, rows: WorkRow[], ownerId?: string) =>
    runBulk(
      rows,
      action,
      {
        claim: async (row, owner) => {
          // `undefined` for the request id, not a fresh one minted here: the
          // service already owns that default, and a second spelling of
          // "generate an idempotency key" is a second thing to get wrong.
          await runLeadAction((row.person.lead as Lead).id, "claim", undefined, owner);
        },
        archive: async (row) => {
          await updateLeadById(supabase, (row.person.lead as Lead).id, { status: "archived" });
        },
        restore: async (row) => {
          // Back to "new" — the status an unworked lead arrives with. It does
          // NOT try to restore a previous status: nothing records what that
          // was, and inventing one would be a guess written to the database.
          await updateLeadById(supabase, (row.person.lead as Lead).id, { status: "new" });
        },
      },
      ownerId,
    ).then((result) => {
      if (result.succeeded > 0) onLeadsChanged();
      return result;
    });

  // Dragging a pill onto a day. CLIENTS only, and on purpose: the living
  // profile records a move-in as an append-only fact with its source, so an
  // operator's correction sits beside the renter's own words instead of
  // overwriting them. A lead has no such ledger — `leads.move_in_date` is what
  // the renter typed, and quietly replacing it would destroy the provenance
  // the whole living profile exists to keep. Convert first; then it is editable.

  return (
    <Stack spacing={3} sx={{ minWidth: 0, maxWidth: "100%", "& .MuiGrid-item": { minWidth: 0 } }} data-testid="leads-page">
      {signals === null && (
        <Typography variant="caption" color="warning.main" data-testid="leads-engagement-degraded">
          The client queue could not be read, so “real client” is decided by stage alone here — someone
          who has already texted back may still be listed below as a lead.
        </Typography>
      )}

      {claimsError && (
        <Typography variant="caption" color="warning.main" data-testid="leads-claims-error">
          The claim ledger could not be read, so nobody is shown as claimed. Counts below are otherwise real.
        </Typography>
      )}

      {claimError && (
        // A refused claim must SAY so. The alternative is a click that appears
        // to do nothing, on the one action standing between a locator and first
        // contact — which is how an operator decides the page is broken.
        <Typography variant="caption" color="error.main" data-testid="leads-claim-error">
          {claimError}
        </Typography>
      )}

      {/* ONE filter bar. The calendar and the work list below read the rows it
          leaves behind, so ticking "Real only" can no longer narrow one half of
          the page while the other half keeps drawing everybody. */}
      <LeadsFilterBar
        filters={filters}
        onToggle={(key) => setFilters((f) => ({ ...f, [key]: !f[key] }))}
        search={search}
        onSearch={setSearch}
        shown={filtered.length}
        total={allRows.length}
        matches={filtered.slice(0, 8).map((r) => ({
          key: r.key,
          name: r.person.name,
          moveInLabel: r.person.moveInText ?? null,
          dateMs: r.person.dateMs,
        }))}
        onPick={(key) => {
          const row = filtered.find((r) => r.key === key);
          if (!row) return;
          // No day to jump to? Then the useful thing is their record, not a
          // calendar page that would show nothing and look like a bug.
          if (row.person.dateMs === null) openPerson(row.person);
          else setFocus({ key, ms: row.person.dateMs });
        }}
      />

      {/* DEFECT A · do not paint a population you are about to change.
          Until GET /clients/queue answers, this page does not know who is a real
          client and who is still a lead — `isEngaged` reads last_inbound_at, consent
          and open invoices, and none of them are on a `clients` row. It used to paint
          the degraded status-only population anyway and then silently shrink it when
          the read landed: Grant watched 218 people become 207 and a lead vanish off
          the calendar, and blamed the filter chip he happened to be clicking.
          My first attempt only LABELLED that ("this list may shrink in a moment"),
          which is honest and still moves the page under the operator. A disclosure is
          not a fix. The population-dependent half of the page waits instead. */}
      {signals === undefined ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }} data-testid="leads-population-pending">
          {/* Ch87 · MOTION. The wait below is correct and stays; what was wrong is
              that it was SILENT and still. A spinner is the difference between
              "working" and "frozen", and it is the whole of what Grant actually
              hit — the page he called broken was mid-read and finished fine. */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={18} thickness={5} data-testid="leads-population-spinner" />
            <Typography variant="body2" color="text.secondary">
              Working out who is still a lead and who is already a client…
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
            This reads the client queue once. Nothing below is drawn until it answers,
            so nobody appears and then disappears.
          </Typography>
          {queueWaitLong && (
            // Says the true thing at the moment it becomes true, rather than
            // leaving the operator to guess from an unchanging screen.
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ display: "block", mt: 1 }}
              data-testid="leads-population-pending-slow"
            >
              This is taking longer than usual — still waiting on the client queue. The
              API may be waking up, which takes about a minute. Nothing is stuck.
            </Typography>
          )}
        </Paper>
      ) : (
        <>
      <LeadsMoveInCalendar
        people={filteredPeople.filter(isCalendarPerson)}
        expiredKeys={expiredKeys}
        onOpen={openPerson}
        focus={focus}
        now={now}
      />

      {/* The analytics range sits WITH the things it governs, not in the page
          header. It is a question about when people ARRIVED — it moves the
          funnel and two of the three cards below, and nothing else on the
          page. Parked up top it read like a page-wide control and silently
          moved numbers three sections away. */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700 }}>
          Analytics
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.disabled">arrived in the last</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={rangeDays}
          onChange={(_, v) => v && setRangeDays(v as number)}
          aria-label="Analytics range"
        >
          {[7, 30, 90].map((d) => (
            <ToggleButton key={d} value={d} data-testid={`leads-range-${d}`}>{d} days</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={7}>
          <LeadClaimInbox
            rows={inboxRows}
            unavailable={claimsError}
            onOpen={openRow}
            onResolved={onLeadsChanged}
            onViewAll={() => workListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <LeadFunnelCard funnel={funnel} loading={funnelLoading} error={funnelError} windowDays={rangeDays} />
        </Grid>
      </Grid>

      <LeadsInsightCards
        sources={sources}
        timing={timing}
        wants={wants}
        rangeDays={rangeDays}
        timingTotal={people.length}
      />

      <Box ref={workListRef} />
      <LeadsWorkList
        groups={groups}
        totalRows={liveCount}
        expiredCount={expiredCount}
        understanding={understanding}
        agentNames={agentNames}
        onOpen={openRow}
        onAction={openRow}
        onSend={(row) => setSendingTo(row)}
        onClaimToText={(row) => void claimToText(row)}
        sender={sender}
        canAssign={canAssign}
        assignableAgents={assignableAgents}
        onBulk={handleBulk}
        now={now}
      />
      </>
      )}


      <SendFromRowDialog
        row={sendingTo}
        senderName={senderName}
        sender={sender}
        onClose={() => setSendingTo(null)}
        onSent={() => {
          // A send changes who has been contacted, which changes stages, next
          // actions and the funnel. Re-read rather than patch a row locally:
          // last_contacted_at is now written by the SERVER, and guessing it
          // here would be the same "log it yourself" problem one layer up.
          onLeadsChanged();
        }}
      />
    </Stack>
  );
}
